import { lazy, Suspense } from 'react';

import type { CartesianChartProps, PieChartProps } from './Charts';

const LazyLineChart = lazy(async () => {
  const mod = await import('./Charts');

  return { default: mod.LineChart };
}) as typeof import('./Charts').LineChart;

const LazyBarChart = lazy(async () => {
  const mod = await import('./Charts');

  return { default: mod.BarChart };
}) as typeof import('./Charts').BarChart;

const LazyPieChart = lazy(async () => {
  const mod = await import('./Charts');

  return { default: mod.PieChart };
}) as typeof import('./Charts').PieChart;

function ChartFallback({ title, height = 280 }: { title?: string; height?: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-card-foreground">
      {title ? <h3 className="mb-4 font-semibold">{title}</h3> : null}
      <div
        role="status"
        aria-label="Loading chart"
        className="flex animate-pulse items-center justify-center rounded-lg bg-muted/40 text-sm text-muted-foreground"
        style={{ height }}
      >
        Loading chart...
      </div>
    </div>
  );
}

/**
 * A simple responsive line chart for trend data already available in code. Use for small,
 * already-loaded datasets. Do not use for live resource record grids.
 *
 * @example
 * <Sdk.Components.LineChart title="Enrollment" data={rows} xKey="month" yKey="count" />
 */
export function LineChart<TRow extends object>(props: CartesianChartProps<TRow>) {
  return (
    <Suspense fallback={<ChartFallback title={props.title} height={props.height} />}>
      <LazyLineChart {...props} />
    </Suspense>
  );
}

/**
 * A simple responsive bar chart for comparing values across categories. Best for compact
 * dashboard summaries.
 *
 * @example
 * <Sdk.Components.BarChart title="Applications by status" data={rows} xKey="status" yKey="total" />
 */
export function BarChart<TRow extends object>(props: CartesianChartProps<TRow>) {
  return (
    <Suspense fallback={<ChartFallback title={props.title} height={props.height} />}>
      <LazyBarChart {...props} />
    </Suspense>
  );
}

/**
 * A simple responsive pie chart for proportional breakdowns. Use with a small number of
 * categories so labels remain understandable.
 *
 * @example
 * <Sdk.Components.PieChart title="Status mix" data={rows} nameKey="status" valueKey="total" />
 */
export function PieChart<TRow extends object>(props: PieChartProps<TRow>) {
  return (
    <Suspense fallback={<ChartFallback title={props.title} height={props.height} />}>
      <LazyPieChart {...props} />
    </Suspense>
  );
}
